import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MdLockOutline, MdWarningAmber, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../../../context/FormContext';
import { useReadOnly } from '../../../hooks/useReadOnly';
import { useCollectJs } from '../../../hooks/useCollectJs';
import { SignupLayout } from '../../ui/SignupLayout';
import { Spinner } from 'reactstrap';
import GoogleAutoComplete from '../../ui/GoogleAutoComplete';

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
    'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
    'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
    'VT','VA','WA','WV','WI','WY'
];

const COUNTRIES = ['USA', 'Canada', 'Mexico'];

export function CardDetails({ history }) {
    // formData is used ONLY to read the mailing address for the
    // "same as mailing" toggle - no payment fields touch context/localStorage
    const { formData, setStep, setFields } = useFormContext();
    const { isReadOnly, readOnlyProps } = useReadOnly();
    const collectJsLoaded = useCollectJs();

    // NON-SENSITIVE payment data only. The card number, expiry, and CVV live
    // exclusively inside the Collect.js hosted-field iframes and never enter React
    // state, router state, context, or localStorage.
    const [payment, setPayment] = useState({
        CreditCardAccountHolderName: '',
        BillingAddressSameAsMailing: true,
        BillingAddressOne: '',
        BillingAddressTwo: '',
        BillingCity: '',
        BillingState: '',
        BillingZip: '',
        BillingCountry: 'USA',
    });

    const [errors, setErrors] = useState({});
    const [tokenizing, setTokenizing] = useState(false);
    // Collect.js reports its hosted fields are mounted;
    const [fieldsReady, setFieldsReady] = useState(false);
    // Per-field validity reported by Collect.js. All must be true before we let the user submit
    const [fieldValid, setFieldValid] = useState({
        ccnumber: false,
        ccexp: false,
        cvv: false,
    });
    const configuredRef = useRef(false);
    const paymentRef = useRef(payment);
    paymentRef.current = payment;

    const setPaymentField = (field, value) =>
        setPayment((prev) => ({ ...prev, [field]: value }));

    const paymentError = history.location?.state?.paymentError || false;
    const paymentErrorMessage = history.location?.state?.paymentErrorMessage || '';

    // Restore NON-SENSITIVE fields if the user navigated back from review.
    useEffect(() => {
        const returned = history.location?.state?.billingInfo;
        if (returned) setPayment(returned);
    }, []);

    useEffect(() => {
        setStep('paymentCard');
    }, []);

    // Configure Collect.js hosted fields once the script is loaded.
    useEffect(() => {
        if (!collectJsLoaded || configuredRef.current || isReadOnly) return;
        if (!window.CollectJS) return;
        configuredRef.current = true;

        window.CollectJS.configure({
            variant: 'inline',
            fields: {
                ccnumber: { selector: '#cj-ccnumber', placeholder: '1234 5678 9012 3456' },
                ccexp: { selector: '#cj-ccexp', placeholder: 'MM / YY' },
                cvv: { selector: '#cj-cvv', placeholder: 'CVV' },
            },
            fieldsAvailableCallback: () => {
                setFieldsReady(true);
            },
            validationCallback: (field, status, message) => {
                setFieldValid((prev) => ({ ...prev, [field]: status }));
                setErrors((prev) => {
                    const next = { ...prev };
                    if (status) delete next[field];
                    else next[field] = message;
                    return next;
                });
            },
            // Fires after startPaymentRequest() when the hosted fields are valid.
            // response.token is the single-use payment_token. We immediately mint
            // the idempotency key and navigate to review carrying token + safe metadata (last four / card type)
            callback: (response) => {
                setTokenizing(false);
                if (!response?.token) {
                    setErrors((prev) => ({ ...prev, _form: 'Could not read your card. Please re-enter and try again.' }));
                    return;
                }

                const idempotencyKey =
                    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
                    `${formData.QuoteId}-${Date.now()}`;
                setFields({ PaymentIdempotencyKey: idempotencyKey });

                history.push('/signup/payment/review', {
                    paymentToken: response.token,
                    paymentType: 'creditCard',
                    billingInfo: paymentRef.current,
                    cardMeta: {
                        last4: response.card?.number ? response.card.number.slice(-4) : '',
                        type: response.card?.type || '',
                    },
                });
            },
        });
    }, [collectJsLoaded, isReadOnly]);

    // -- Billing address autocomplete
    const handleBillingAddress1Change = useCallback((value) => {
        setPaymentField('BillingAddressOne', value);
    }, []);

    const handleBillingPlaceSelected = useCallback((place) => {
        if (!place?.address_components) return;
        const comp = place.address_components;
        const get = (type) => comp.find((c) => c.types.includes(type)) || {};

        setPayment((prev) => ({
            ...prev,
            BillingAddressOne: `${get('street_number').long_name || ''} ${get('route').long_name || ''}`.trim(),
            BillingAddressTwo: get('subpremise').long_name || '',
            BillingCity: get('locality').long_name || get('sublocality').long_name || '',
            BillingState: get('administrative_area_level_1').short_name || '',
            BillingZip: get('postal_code').long_name || '',
            BillingCountry: 'USA',
            BillingAddressSameAsMailing: false,
        }));
    }, []);

    // -- Same as mailing toggle
    const handleSameAsMailing = (checked) => {
        if (checked) {
            setPayment((prev) => ({
                ...prev,
                BillingAddressSameAsMailing: true,
                BillingAddressOne: formData.ContactAddressOne || '',
                BillingAddressTwo: formData.ContactAddressTwo || '',
                BillingCity: formData.ContactCity || '',
                BillingState: formData.ContactState || '',
                BillingZip: formData.ContactZipCode || '',
                BillingCountry: formData.ContactCountry || 'USA',
            }));
        } else {
            setPayment((prev) => ({
                ...prev,
                BillingAddressSameAsMailing: false,
                BillingAddressOne: '',
                BillingAddressTwo: '',
                BillingCity: '',
                BillingState: '',
                BillingZip: '',
                BillingCountry: 'USA',
            }));
        }
    };

    // -- Validation of the NON-SENSITIVE fields.
    // Card-field validity is enforced by Collect.js itself (validationCallback + tokenization).
    const validate = () => {
        const e = {};
        if (!payment.CreditCardAccountHolderName?.trim())
            e.CreditCardAccountHolderName = 'Cardholder name is required.';
        if (!payment.BillingAddressSameAsMailing) {
            if (!payment.BillingAddressOne?.trim())
                e.BillingAddressOne = 'Billing address is required.';
            if (!payment.BillingCity?.trim())
                e.BillingCity = 'City is required.';
            if (!payment.BillingState?.trim())
                e.BillingState = 'State is required.';
            if (!payment.BillingZip?.trim())
                e.BillingZip = 'ZIP code is required.';
        }
        return e;
    };

    const handleReview = () => {
        if (isReadOnly) {
            history.push('/signup/payment/review');
            return; // skip validation, tokenization, everything
        }
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setErrors({});

        if (!fieldsReady || !window.CollectJS) {
            setErrors({ _form: 'Secure card fields are still loading. Please try again in a moment.' });
            return;
        }

        // Trigger tokenization. The result arrives in the configure() callback,
        // which performs the navigation to /signup/payment/review.
        setTokenizing(true);
        window.CollectJS.startPaymentRequest();
    };

    const handleBack = () => history.push('/signup/payment');

    // All hosted card fields must be valid before Review can be clicked.
    const allCardFieldsValid = fieldValid.ccnumber && fieldValid.ccexp && fieldValid.cvv;

    return (
        <SignupLayout showProgress={false}>

            {/* Payment error banner */}
            {paymentError && (
                <div className="payment-error-banner">
                    <MdWarningAmber size={20} className="payment-error-banner__icon" />
                    <div>
                        <strong>Payment unsuccessful</strong>
                        <p>{paymentErrorMessage || "We couldn't process your payment. Review your details below and resubmit."}</p>
                    </div>
                </div>
            )}

            <h1 className="signup-heading">Enter credit card details.</h1>
            <p className="signup-section__desc">
                Please provide your payment information to complete your policy purchase.
            </p>

            {errors._form && (
                <span className="error-text field-error" role="alert">{errors._form}</span>
            )}

            {!fieldsReady && (
                <div className="dp-loading" aria-label="Loading payment fields…">
                    <Spinner />
                </div>
            )}

            <div
                style={{
                    opacity: fieldsReady ? 1 : 0,
                    transition: 'opacity 250ms ease',
                    // pointerEvents: fieldsReady ? 'auto' : 'none'
                }}
            >
                <div className="form-group">
                    <p className="signup-field-label"><strong>Cardholder name</strong></p>
                    <input
                        type="text"
                        placeholder="John Doe"
                        value={payment.CreditCardAccountHolderName}
                        onChange={(e) => setPaymentField('CreditCardAccountHolderName', e.target.value)}
                        {...readOnlyProps}
                    />
                    {errors.CreditCardAccountHolderName && (
                        <span className="error-text field-error">{errors.CreditCardAccountHolderName}</span>
                    )}
                </div>

                {/* Card number - Collect.js hosted iframe */}
                <div className="form-group">
                    <p className="signup-field-label"><strong>Card number</strong></p>
                    <div id="cj-ccnumber" className="collectjs-field" />
                    {errors.ccnumber && (
                        <span className="error-text field-error">{errors.ccnumber}</span>
                    )}
                </div>

                <div className="card-row">
                    <div className="form-group" style={{ flex: 1 }}>
                        <p className="signup-field-label"><strong>Expiration date</strong></p>
                        <div id="cj-ccexp" className="collectjs-field" />
                        {errors.ccexp && (
                            <span className="error-text field-error">{errors.ccexp}</span>
                        )}
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                        <p className="signup-field-label"><strong>CVV</strong></p>
                        <div id="cj-cvv" className="collectjs-field" />
                        {errors.cvv && (
                            <span className="error-text field-error">{errors.cvv}</span>
                        )}
                    </div>
                </div>
            </div>

            <label className="checklist__item" style={{ margin: 'var(--space-sm) 0' }} {...readOnlyProps}>
                <input
                    type="checkbox"
                    checked={payment.BillingAddressSameAsMailing}
                    onChange={(e) => handleSameAsMailing(e.target.checked)}
                    className="checklist__input"
                    {...readOnlyProps}
                />
                <span className="checklist__box">
                    {payment.BillingAddressSameAsMailing && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                </span>
                <span className="checklist__label">
                    Billing address is the same as the mailing address.
                </span>
            </label>

            {!payment.BillingAddressSameAsMailing && (
                <div className="billing-address-form">
                    <p className="signup-field-label" style={{ marginBottom: 'var(--space-xs)' }}>
                        <strong>Enter your billing address.</strong>
                        <span style={{ color: 'var(--error-500)', marginLeft: '2px' }}>*</span>
                    </p>
                    <div className="form-group">
                        <GoogleAutoComplete
                            value={payment.BillingAddressOne}
                            onChange={handleBillingAddress1Change}
                            onPlaceSelected={handleBillingPlaceSelected}
                            placeholder="Address 1"
                            {...readOnlyProps}
                        />
                        {errors.BillingAddressOne && (
                            <span className="error-text field-error">{errors.BillingAddressOne}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            placeholder="Address 2"
                            value={payment.BillingAddressTwo}
                            onChange={(e) => setPaymentField('BillingAddressTwo', e.target.value)}
                            {...readOnlyProps}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            placeholder="City"
                            value={payment.BillingCity}
                            onChange={(e) => setPaymentField('BillingCity', e.target.value)}
                            {...readOnlyProps}
                        />
                        {errors.BillingCity && (
                            <span className="error-text field-error">{errors.BillingCity}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <select
                            value={payment.BillingState}
                            onChange={(e) => setPaymentField('BillingState', e.target.value)}
                            {...readOnlyProps}
                        >
                            <option value="">Select state</option>
                            {US_STATES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                        {errors.BillingState && (
                            <span className="error-text field-error">{errors.BillingState}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            placeholder="Zip code"
                            value={payment.BillingZip}
                            onChange={(e) => setPaymentField('BillingZip', e.target.value)}
                            maxLength={10}
                            {...readOnlyProps}
                        />
                        {errors.BillingZip && (
                            <span className="error-text field-error">{errors.BillingZip}</span>
                        )}
                    </div>
                    <div className="form-group">
                        <select
                            value={payment.BillingCountry}
                            onChange={(e) => setPaymentField('BillingCountry', e.target.value)}
                            {...readOnlyProps}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="security-banner">
                <MdLockOutline size={20} className="security-banner__icon" />
                <div className="security-banner__text">
                    <strong>Pay with confidence.</strong>
                    <p>Your card details are encrypted and processed securely. We never store your CVV.</p>
                </div>
            </div>

            <div className="signup-nav">
                <button className="btn signup-cta btn--inverse-cta" onClick={handleBack} disabled={tokenizing}>
                    <MdArrowBack /> Back
                </button>
                <button className="btn signup-cta" onClick={handleReview} disabled={tokenizing || (!isReadOnly && (!fieldsReady || !allCardFieldsValid))}>
                    {tokenizing ? 'Securing…' : <>Review <MdArrowForward /></>}
                </button>
            </div>
        </SignupLayout>
    );
}